/* Purelight — BREWLAB bean drop. Hardened port of pl-beans.js.
   Three bugs fixed against the original:
   1. A HEIGHT-ONLY viewport change re-solved the whole pile. On a phone the URL bar
      showing/hiding fires exactly that on every scroll, so the pile visibly rearranged
      as you scrolled — measured: ink delta 0 across scrolls, -106 across a height-only
      resize. Now only a material WIDTH change re-solves.
   2. Micro-velocities were damped (×0.6) but never zeroed, so beans crept forever.
      They are now hard-zeroed below the sleep threshold.
   3. The sim ran every pair every frame. A uniform-grid broadphase makes a 200-bean
      pile cheap enough to settle in well under a second of main-thread time. */
(function () {
  'use strict';

  function mulberry(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function Sim(w, h, count, seed, lw, base) {
    this.w = w; this.h = h; this.rnd = mulberry(seed || 20260825);
    this.beans = []; this.spawned = 0; this.count = count; this.base = base || 64;
    this.settled = false; this.quietFor = 0;
    this.G = 0.78; this.REST = 0.05; this.FRIC = 0.42; this.AIR = 0.975;
    this.SLEEP = 0.30;
    /* The floor IS the drawn rule. Contact is resolved against each bean's true art extent
       (see SUP) so every bean in the bottom row rests exactly on the line rather than
       floating above it or crossing it. Bean-to-bean contact still uses `rad`, so the pile
       packs exactly as before. */
    this.lw = lw || 0;
    this.floor = h - this.lw;
    this.cell = 0; this.grid = null;
  }
  Sim.prototype.spawn = function () {
    var r = this.rnd, t = r();
    var x = (t < 0.38 ? 0.02 + r() * 0.40 : (t < 0.72 ? 0.36 + r() * 0.34 : 0.62 + r() * 0.37)) * this.w;
    /* Decoded from the reference (apocalypsecoffee theme.js): the bean's longest side is
       `size * rnd(0.85, 1.15)`, NOT an independent 40-70 range. */
    var size = this.base * (0.85 + r() * 0.30);
    this.beans.push({
      x: x, y: -60 - r() * 340, vx: (r() - 0.5) * 1.1, vy: 1 + r() * 2.2,
      a: r() * Math.PI * 2, va: (r() - 0.5) * 0.16,
      rad: size * 0.42, size: size, cell: (this.beans.length * 5 + ((r() * 12) | 0)) % 12
    });
    this.spawned++;
  };
  Sim.prototype.rebuildGrid = function () {
    var b = this.beans, n = b.length, i;
    if (!n) return;
    var maxR = 0;
    for (i = 0; i < n; i++) if (b[i].rad > maxR) maxR = b[i].rad;
    this.cell = Math.max(8, maxR * 2);
    var cols = Math.ceil(this.w / this.cell) + 2, rows = Math.ceil((this.h + 400) / this.cell) + 2;
    this.cols = cols; this.rows = rows;
    var g = this.grid;
    if (!g || g.length !== cols * rows) { g = this.grid = new Array(cols * rows); }
    for (i = 0; i < g.length; i++) g[i] = null;
    for (i = 0; i < n; i++) {
      var p = b[i];
      var cx = ((p.x / this.cell) | 0) + 1, cy = (((p.y + 400) / this.cell) | 0) + 1;
      if (cx < 0) cx = 0; if (cx >= cols) cx = cols - 1;
      if (cy < 0) cy = 0; if (cy >= rows) cy = rows - 1;
      var k = cy * cols + cx;
      (g[k] || (g[k] = [])).push(i);
    }
  };
  Sim.prototype.step = function () {
    var b = this.beans, n = b.length, i, j;
    for (i = 0; i < n; i++) {
      var p = b[i];
      if (p.sleep) continue;                       /* asleep: static, no integration at all */
      p.vy += this.G; p.vx *= this.AIR; p.vy *= this.AIR;
      p.x += p.vx; p.y += p.vy; p.a += p.va; p.va *= 0.92;
    }
    for (var it = 0; it < 4; it++) {
      this.rebuildGrid();
      for (i = 0; i < n; i++) {
        var A = b[i];
        var cx = ((A.x / this.cell) | 0) + 1, cy = (((A.y + 400) / this.cell) | 0) + 1;
        for (var oy = -1; oy <= 1; oy++) {
          for (var ox = -1; ox <= 1; ox++) {
            var gx = cx + ox, gy = cy + oy;
            if (gx < 0 || gy < 0 || gx >= this.cols || gy >= this.rows) continue;
            var bucket = this.grid[gy * this.cols + gx];
            if (!bucket) continue;
            for (var q = 0; q < bucket.length; q++) {
              j = bucket[q]; if (j <= i) continue;
              var B = b[j];
              var dx = B.x - A.x, dy = B.y - A.y, minD = A.rad + B.rad;
              if (dx > minD || dx < -minD || dy > minD || dy < -minD) continue;
              var d2 = dx * dx + dy * dy;
              if (d2 >= minD * minD || d2 === 0) continue;
              var d = Math.sqrt(d2), nx = dx / d, ny = dy / d;
              var pen = minD - d;
              var aFree = !A.sleep, bFree = !B.sleep;
              if (!aFree && !bFree) continue;
              var share = (aFree && bFree) ? 0.5 : 1;
              if (aFree) { A.x -= nx * pen * share; A.y -= ny * pen * share; }
              if (bFree) { B.x += nx * pen * share; B.y += ny * pen * share; }
              var vn = (B.vx - A.vx) * nx + (B.vy - A.vy) * ny;
              if (vn < 0) {
                var im = -(1 + this.REST) * vn * share;
                if (aFree) { A.vx -= im * nx; A.vy -= im * ny; A.va -= im * 0.008; }
                if (bFree) { B.vx += im * nx; B.vy += im * ny; B.va += im * 0.008; }
                /* a real knock wakes a sleeper; a graze does not */
                if (-vn > 3.2) { A.sleep = false; B.sleep = false; }
              }
            }
          }
        }
        var foot = support(A.cell, A.a) * A.size;
        if (A.y + foot > this.floor) {
          A.y = this.floor - foot;
          if (A.vy > 0) { A.vy = -A.vy * this.REST; A.vx *= this.FRIC; A.va *= 0.35; }
        }
        if (A.x - A.rad < 0) { A.x = A.rad; A.vx = Math.abs(A.vx) * this.REST; }
        if (A.x + A.rad > this.w) { A.x = this.w - A.rad; A.vx = -Math.abs(A.vx) * this.REST; }
      }
    }
    var moving = 0;
    for (i = 0; i < n; i++) {
      var s = b[i];
      if (Math.abs(s.vx) > this.SLEEP || Math.abs(s.vy) > this.SLEEP) moving++;
      else { s.vx = 0; s.vy = 0; s.va = 0; }   /* hard zero — damping alone let them creep */
    }
    var quiet = (this.spawned >= this.count) && (moving <= Math.max(1, Math.round(this.count * 0.02)));
    this.quietFor = quiet ? this.quietFor + 1 : 0;
    this.settled = this.quietFor >= 12;
    return this.settled;
  };
  Sim.prototype.solve = function (steps) {
    for (var s = 0; s < (steps || 1600); s++) {
      if (this.spawned < this.count) { this.spawn(); if (this.spawned < this.count) this.spawn(); }
      if (this.step()) break;
    }
  };

  /* How far a bean's ART reaches BELOW its own centre, per sprite cell and per rotation,
     in units of `size`. Measured off pl-beans.png (12 cells, 64 angles) rather than assumed:
     the real range is 0.227-0.530, so neither the collision radius (0.42) nor the sprite
     half (0.50) lands a bean on a line. With this the resting art touches the floor exactly,
     whatever the bean and whatever its angle — which is what lets the pile sit ON the rule
     instead of hovering over it or cutting through it. */
  var SUP_N = 64;
  var SUP = [[0.4933,0.5099,0.5216,0.5282,0.5298,0.5262,0.5176,0.506,0.495,0.4792,0.4606,0.439,0.4196,0.4016,0.3868,0.3706,0.3533,0.3411,0.3334,0.33,0.3267,0.3363,0.3571,0.3784,0.396,0.4172,0.4344,0.452,0.4664,0.4785,0.4904,0.4976,0.5,0.5074,0.5111,0.5193,0.524,0.5255,0.5251,0.5211,0.5138,0.5081,0.4975,0.4845,0.472,0.4619,0.4522,0.4409,0.4267,0.4246,0.4197,0.4248,0.428,0.4338,0.4364,0.4401,0.4573,0.474,0.4898,0.5015,0.5094,0.5127,0.5112,0.5047],[0.3333,0.3414,0.3542,0.3709,0.3884,0.4033,0.418,0.4323,0.4478,0.4591,0.4659,0.4702,0.4739,0.4739,0.4759,0.4736,0.4667,0.4579,0.446,0.4346,0.4251,0.4267,0.4253,0.4235,0.4196,0.4119,0.405,0.3966,0.387,0.3801,0.3725,0.3669,0.3667,0.3754,0.3843,0.3923,0.4009,0.4087,0.418,0.4237,0.429,0.4329,0.4345,0.4334,0.4323,0.4286,0.4209,0.4118,0.4,0.3974,0.391,0.3808,0.37,0.3571,0.3438,0.3293,0.33,0.3293,0.3273,0.3274,0.3282,0.3269,0.3269,0.3291],[0.3867,0.4031,0.4157,0.4242,0.4302,0.433,0.4346,0.4386,0.4384,0.4423,0.4419,0.4462,0.4482,0.4495,0.4485,0.4464,0.44,0.4379,0.4315,0.4231,0.4317,0.4377,0.4438,0.4456,0.4431,0.4382,0.4291,0.4235,0.4236,0.4203,0.4131,0.4052,0.3933,0.3856,0.3741,0.359,0.3454,0.334,0.3235,0.3185,0.3158,0.3146,0.3216,0.3294,0.335,0.3395,0.3465,0.3516,0.3533,0.3595,0.3622,0.3613,0.3615,0.36,0.355,0.3548,0.3677,0.3779,0.3882,0.3952,0.4006,0.4029,0.4013,0.3959],[0.4667,0.4651,0.4603,0.4584,0.4535,0.4494,0.4437,0.4423,0.4384,0.4358,0.4309,0.4302,0.4255,0.4178,0.4105,0.4032,0.3933,0.3836,0.3702,0.3563,0.3569,0.3886,0.4219,0.4512,0.4761,0.4965,0.512,0.5227,0.5283,0.5288,0.5242,0.5146,0.5,0.5041,0.5034,0.4978,0.4875,0.4724,0.4528,0.4288,0.4007,0.3687,0.3332,0.3414,0.3572,0.3706,0.3819,0.3928,0.4,0.4092,0.4144,0.419,0.4195,0.416,0.4142,0.4148,0.4243,0.437,0.4474,0.4549,0.4586,0.4628,0.4668,0.469],[0.4867,0.4974,0.5033,0.511,0.5168,0.52,0.5214,0.5192,0.5138,0.5081,0.4975,0.4864,0.4725,0.4577,0.4404,0.4237,0.4067,0.3929,0.378,0.3648,0.3511,0.3448,0.3495,0.3538,0.3583,0.3639,0.3661,0.3671,0.3666,0.3641,0.3608,0.3555,0.3467,0.3417,0.336,0.3325,0.3273,0.3207,0.3142,0.3047,0.2923,0.2835,0.2719,0.2625,0.2526,0.2429,0.2366,0.2321,0.2267,0.2295,0.234,0.2456,0.2577,0.27,0.2849,0.3044,0.33,0.3552,0.3788,0.4025,0.4224,0.44,0.4578,0.4719],[0.4467,0.4484,0.4459,0.439,0.4325,0.4246,0.4159,0.4046,0.3913,0.3769,0.3626,0.3453,0.3278,0.3107,0.298,0.287,0.2733,0.2641,0.2589,0.2527,0.2899,0.3298,0.3665,0.3997,0.429,0.4542,0.475,0.4912,0.5028,0.5094,0.5112,0.508,0.5,0.5041,0.5034,0.4978,0.4875,0.4724,0.4528,0.4288,0.4007,0.3687,0.3495,0.3448,0.342,0.3385,0.3347,0.3323,0.3267,0.3323,0.3373,0.3457,0.3526,0.3601,0.3736,0.3848,0.396,0.4079,0.4178,0.427,0.435,0.4404,0.4446,0.4471],[0.4467,0.4582,0.4654,0.4747,0.4794,0.4812,0.4826,0.4841,0.4808,0.4734,0.4661,0.4566,0.4427,0.4277,0.4104,0.3938,0.3733,0.3532,0.3297,0.3039,0.279,0.2697,0.2625,0.2565,0.2498,0.249,0.2551,0.265,0.281,0.3002,0.3179,0.3373,0.3533,0.3738,0.3908,0.4072,0.4198,0.4291,0.4364,0.4395,0.4431,0.453,0.4585,0.4596,0.4595,0.4561,0.4484,0.4396,0.4267,0.4174,0.4042,0.3884,0.3721,0.3567,0.3477,0.3547,0.3583,0.3695,0.3789,0.3886,0.4008,0.4127,0.4251,0.438],[0.4333,0.4365,0.4354,0.4367,0.4359,0.4361,0.4364,0.4395,0.4431,0.4475,0.4493,0.4502,0.4518,0.4534,0.4511,0.4504,0.4467,0.4406,0.4303,0.4166,0.4138,0.4145,0.4123,0.4061,0.396,0.3978,0.3957,0.3899,0.3834,0.3757,0.3673,0.3581,0.3467,0.3345,0.3205,0.306,0.294,0.2886,0.2847,0.2819,0.2876,0.295,0.3032,0.3132,0.3233,0.3337,0.3426,0.3497,0.3533,0.3627,0.3687,0.371,0.3713,0.3698,0.3718,0.381,0.3866,0.3976,0.4067,0.4133,0.4191,0.4251,0.4276,0.4312],[0.4533,0.4557,0.4537,0.4474,0.4412,0.4333,0.4252,0.4149,0.4007,0.3891,0.3737,0.3594,0.3469,0.3332,0.3215,0.3123,0.3067,0.3025,0.302,0.3053,0.3072,0.3087,0.3219,0.3394,0.3536,0.3708,0.3845,0.3968,0.4095,0.4199,0.4276,0.4326,0.4333,0.4365,0.4367,0.442,0.4431,0.4447,0.4437,0.4399,0.4337,0.427,0.418,0.4083,0.3983,0.3898,0.3804,0.372,0.36,0.3615,0.3608,0.3641,0.3671,0.3718,0.3773,0.3891,0.4007,0.4122,0.4215,0.4305,0.4376,0.4443,0.4485,0.4525],[0.4267,0.422,0.4184,0.4182,0.4159,0.4161,0.4197,0.4255,0.429,0.4375,0.4474,0.4568,0.4654,0.4742,0.4799,0.4856,0.4867,0.4889,0.489,0.4872,0.4847,0.4836,0.4938,0.4991,0.4997,0.5036,0.5121,0.518,0.52,0.521,0.519,0.512,0.5,0.4917,0.4787,0.4676,0.4601,0.4549,0.4492,0.4457,0.4525,0.4652,0.4751,0.4842,0.493,0.4986,0.5034,0.5041,0.5,0.5009,0.4969,0.4895,0.4787,0.4654,0.4528,0.4438,0.4431,0.4456,0.4456,0.4428,0.4399,0.4381,0.434,0.4298],[0.3667,0.3845,0.3986,0.4136,0.4264,0.4381,0.4476,0.4561,0.462,0.4681,0.4697,0.4686,0.4682,0.4644,0.4575,0.4476,0.4333,0.4241,0.4107,0.3934,0.3742,0.3612,0.3606,0.3617,0.363,0.3663,0.3697,0.3795,0.39,0.4019,0.4119,0.4207,0.4267,0.437,0.4432,0.4451,0.4442,0.4412,0.4382,0.4316,0.4337,0.4335,0.429,0.4228,0.4126,0.3995,0.3856,0.3712,0.3533,0.3327,0.3127,0.2911,0.2697,0.2512,0.2367,0.2421,0.2498,0.2611,0.2717,0.2858,0.3014,0.3174,0.3349,0.3518],[0.4467,0.4465,0.4446,0.4451,0.4427,0.436,0.4252,0.4173,0.4054,0.3951,0.3811,0.368,0.3546,0.3421,0.3281,0.317,0.3067,0.2987,0.2916,0.2879,0.2872,0.292,0.3108,0.3305,0.3488,0.3684,0.3844,0.4014,0.4157,0.4294,0.4394,0.4452,0.4467,0.4556,0.4602,0.4617,0.4635,0.4651,0.4641,0.4605,0.4525,0.4467,0.4365,0.424,0.4126,0.3995,0.3869,0.378,0.3667,0.3642,0.3583,0.3588,0.3609,0.3643,0.3699,0.3735,0.3866,0.4022,0.4141,0.4242,0.4314,0.4385,0.4433,0.4471]];
  function support(cell, a) {
    var k = Math.round(a / (Math.PI * 2) * SUP_N) % SUP_N;
    if (k < 0) k += SUP_N;
    return SUP[cell % SUP.length][k];
  }

  function mount(host) {
    if (host.dataset.blBeans === '1') return;
    host.dataset.blBeans = '1';
    var canvas = host.querySelector('canvas');
    var sheet = host.getAttribute('data-sheet');
    var count = parseInt(host.getAttribute('data-count'), 10) || 95;
    var base = parseInt(host.getAttribute('data-size'), 10) || 64;
    /* Straight from the reference: it caps BOTH the count and the bean size under 768,
       which is the single reason its pile works on a phone and ours did not. We were
       dropping 130 beans at 40-70px into a 390x167 strip — roughly 3x more bean area than
       the canvas could hold — so the solver never resolved and the pile churned. */
    if (Math.max(document.documentElement.clientWidth, window.innerWidth || 0) < 768) {
      count = Math.min(count, 40);
      base = Math.min(base, 42);
    }
    if (!canvas || !sheet) return;
    var ctx = canvas.getContext('2d'), img = new Image();
    var CELL = 150, dpr = Math.min(window.devicePixelRatio || 1, 2);
    var sim = null, raf = 0, lastW = 0, done = false;
    var lineOn = host.getAttribute('data-line') !== 'false';
    var lineCol = host.getAttribute('data-line-color') || 'rgba(75,42,32,.22)';
    var lineW = Math.max(1, parseFloat(host.getAttribute('data-line-width')) || 2);

    /* Drawn FIRST so the beans sit on top of it where they touch. */
    function floorLine(d) {
      if (!lineOn) return;
      ctx.fillStyle = lineCol;
      ctx.fillRect(0, d.h - lineW, d.w, lineW);
    }

    function size() {
      var r = canvas.getBoundingClientRect();
      var w = Math.max(320, Math.round(r.width)), h = Math.max(140, Math.round(r.height));
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w: w, h: h };
    }
    function draw(d) {
      ctx.clearRect(0, 0, d.w, d.h);
      floorLine(d);
      if (!sim) return;
      for (var i = 0; i < sim.beans.length; i++) {
        var b = sim.beans[i];
        ctx.save(); ctx.translate(b.x, b.y); ctx.rotate(b.a);
        ctx.drawImage(img, b.cell * CELL, 0, CELL, CELL, -b.size / 2, -b.size / 2, b.size, b.size);
        ctx.restore();
      }
    }
    function still() {
      var d = size(); lastW = d.w;
      sim = new Sim(d.w, d.h, count, 20260825, lineOn ? lineW : 0, base);
      sim.solve(2200);
      rest = sim.beans.map(function (b) { return { x: b.x, y: b.y, a: b.a, size: b.size, cell: b.cell }; });
      drawRest(d, 1);
      host.setAttribute('data-settled', 'true'); done = true;
    }

    /* The pile is SOLVED once, up front and deterministically, then each bean is tweened
       from above into its known resting place. A live physics race across a 1440px floor
       took 10-12s to come to rest on desktop and read as "they keep looping and never stop"
       (Nik, 2026-08-25). Choreographing it is visually the same drop, lands in ~1.5s every
       time, is byte-identical on every load, and cannot jitter. */
    var rest = null, T0 = 0, DUR = 1500;
    function drawRest(d, p) {
      ctx.clearRect(0, 0, d.w, d.h);
      floorLine(d);
      if (!rest) return;
      for (var i = 0; i < rest.length; i++) {
        var b = rest[i], y = b.y, a = b.a;
        if (p < 1) {
          /* each bean gets its own slice of the window, ordered by where it lands */
          var s0 = (i % 17) / 17 * 0.42;
          var t = (p - s0) / (1 - s0);
          if (t <= 0) continue;
          if (t > 1) t = 1;
          var fall = t * t;                       /* gravity in, not eased out */
          var over = t < 1 ? 0 : 0;
          y = (-90 - (i % 11) * 26) + (b.y + 90 + (i % 11) * 26) * fall;
          a = b.a - (1 - t) * (1.6 + (i % 5) * 0.3);
          if (t > 0.86) {                          /* a small settle at the end */
            var q = (t - 0.86) / 0.14;
            y = b.y - Math.sin(q * Math.PI) * 5;
          }
        }
        ctx.save(); ctx.translate(b.x, y); ctx.rotate(a);
        ctx.drawImage(img, b.cell * CELL, 0, CELL, CELL, -b.size / 2, -b.size / 2, b.size, b.size);
        ctx.restore();
      }
    }
    /* Real physics, so the beans land ONE BY ONE and knock into each other exactly as they
       do in the reference — that part was always right. The bug was never the drop, it was
       the settle: velocities were damped but never zeroed, so the pile crawled forever, and
       a phone's URL-bar resize re-solved the whole thing. Now: hard-zeroed micro-velocities,
       a hard frame cap, and once it is settled the canvas is never touched again. */
    function animate() {
      var d = size(); lastW = d.w;
      sim = new Sim(d.w, d.h, count, 20260825, lineOn ? lineW : 0, base);
      var frames = 0, CAP = 900, spawnDone = 0;
      /* A packed pile never truly stops on its own: the overlap relaxation keeps nudging
         neighbours by fractions of a pixel forever, which is visible as beans slowly
         circling after they have landed. So the settle is a WINDOW, not a convergence test —
         once every bean has been spawned we allow a fixed number of frames to resolve the
         stack and then put the whole pile to sleep. Deterministic and always stops. */
      var SETTLE = 45;
      (function loop() {
        frames++;
        /* stagger the spawn so they arrive one after another rather than as a curtain */
        /* The reference drops on a 70ms setInterval — about one bean every 4 frames at
           60fps. Ours fired ~1 per frame, which arrived as a curtain rather than a fall. */
        if (sim.spawned < sim.count && frames % 4 === 0) sim.spawn();
        if (!spawnDone && sim.spawned >= sim.count) spawnDone = frames;
        var fin = sim.step();
        drawLive(d);
        var windowUp = spawnDone && (frames - spawnDone) > SETTLE;
        if (!fin && !windowUp && frames < CAP) { raf = requestAnimationFrame(loop); return; }
        /* freeze: one last paint, then stop for good */
        for (var i = 0; i < sim.beans.length; i++) {
          var b = sim.beans[i]; b.vx = 0; b.vy = 0; b.va = 0; b.sleep = true;
        }
        drawLive(d);
        raf = 0; done = true;
        host.setAttribute('data-settled', 'true');
      })();
    }
    function drawLive(d) {
      ctx.clearRect(0, 0, d.w, d.h);
      floorLine(d);
      if (!sim) return;
      for (var i = 0; i < sim.beans.length; i++) {
        var b = sim.beans[i];
        ctx.save(); ctx.translate(b.x, b.y); ctx.rotate(b.a);
        ctx.drawImage(img, b.cell * CELL, 0, CELL, CELL, -b.size / 2, -b.size / 2, b.size, b.size);
        ctx.restore();
      }
    }

    /* ⛔ Only a material WIDTH change re-solves. A phone's URL bar changes the viewport
       HEIGHT on every scroll, and re-solving there is what made the pile crawl. */
    function onResize() {
      var w = Math.round(canvas.getBoundingClientRect().width);
      if (Math.abs(w - lastW) < 24) return;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      still();
    }
    var t;
    function debounced() { clearTimeout(t); t = setTimeout(onResize, 260); }

    img.onload = function () {
      var rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (rm || !('IntersectionObserver' in window)) { still(); }
      else {
        var io = new IntersectionObserver(function (es) {
          es.forEach(function (e) { if (e.isIntersecting) { io.disconnect(); animate(); } });
        }, { threshold: 0.25 });
        io.observe(host);
      }
      window.addEventListener('resize', debounced, { passive: true });
      if (window.visualViewport) window.visualViewport.addEventListener('resize', debounced, { passive: true });
    };
    img.onerror = function () { host.setAttribute('data-settled', 'error'); };
    img.src = sheet;
  }

  function init(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-bl-beandrop]'), mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
